import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { fade } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	PropertyType,
	MatterTitle,
} from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import { MatterFCTResidentialTitleInsurance } from '@models/title-insurance/fct';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import LogRocket from 'logrocket';
import { Observable, combineLatest } from 'rxjs';
import { Step } from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';
import { BlankTitleInsurance } from '../../chicago/chicago.component';
import { AddTitleInsuranceComponent } from '../../components/add-title-insurance/add-title-insurance.component';
import {
	CostBreakdownComponent,
	FCTTitleInsuranceCostBreakdownData,
} from '../components/cost-breakdown/cost-breakdown.component';
import { FctCancelModalComponent } from '../components/fct-cancel-modal/fct-cancel-modal.component';
import { FctPortalComponent } from '../components/fct-portal/fct-portal.component';
import { FctGetStatusService } from '../services/fct-get-status.service';
import {
	MissingFieldSection,
	ValidateFctDataService,
} from '../services/validate-fct-data.service';
import { filter } from 'rxjs/operators';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { isRegionOntario } from '@core/util/address.utils';
import { TestHooks } from './residential-fct.hooks';
import { FCTTitleInsuranceState } from '@models/title-insurance';

const Steps: Step[] = [
	{ title: 'Preparing' },
	{ title: 'Submitted' },
	{ title: 'Processing' },
	{ title: 'Completed' },
];

@UntilDestroy()
@Component({
	selector: 'app-residential-fct',
	templateUrl: './residential-fct.component.html',
	styleUrls: ['./residential-fct.component.scss'],
	animations: [fade()],
	standalone: false,
})
export class ResidentialFctComponent implements OnInit {
	testHooks = TestHooks;
	public ICON = ICON;
	public currentStep = 0;
	public steps = Steps;
	public state: FCTTitleInsuranceState;
	public states = FCTTitleInsuranceState;

	public amountDueByClient: number;
	public extendCreditToClient: boolean;

	public orderValid = false;
	public isCommercial = false;
	public invalidProperty = false;
	public outOfOntario = false;
	public quoteValid = false;
	public FCTState = FCTTitleInsuranceState;
	public missing: MissingFieldSection[] = [];
	public titleInsurance: MatterFCTResidentialTitleInsurance;
	public hasQuote: boolean;
	public loading = false;
	public propertyUsage: PropertyUsage;
	public usageType = PropertyUsage;

	public blankTitleInsurance = BlankTitleInsurance;

	@Select(SelectedMatter.getTitleInsuranceState)
	state$: Observable<FCTTitleInsuranceState>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	@Select(SelectedMatter.getTitleInsurance)
	titleInsurance$: Observable<MatterFCTResidentialTitleInsurance>;
	@Select(SelectedMatter.getSelected) matter$: Observable<Matter>;
	@Select(MatterClients.get_mortgagorsWithAssociationMapped)
	clients$: Observable<(PersonMatterClient | BusinessMatterClient)[]>;

	@SelectSnapshot(SelectedMatter.titleInsuranceThirdPartyId)
	thirdPartyId: string;
	@Select(MatterMortgagesState.getAllMortgagesWithAssociations)
	mortgages$: Observable<MatterMortgageWithAssociations[]>;
	@Select(SelectedMatter.titleInsuranceQuoteAmountDue)
	quoteAmountDue$: Observable<number>;
	@ViewSelectSnapshot(SelectedMatter.titleInsurancePortalUrl) portalUrl: string;
	@ViewSelectSnapshot(SelectedMatter.titleInsuranceBasePremium)
	basePremium: string;
	@Select(MatterTitlesState.getAll) matterTitles$: Observable<MatterTitle[]>;
	@Select(SelectedMatter.titleInsuranceEffectiveDate)
	effectiveDate$: Observable<string>;

	@Output() onChangeProvider = new EventEmitter<void>();

	constructor(
		private route: ActivatedRoute,
		private validateFctDataService: ValidateFctDataService,
		private dialog: MatDialog,
		private matterDispatch: MatterDispatch,
		private fctGetStatusService: FctGetStatusService,
	) {}

	ngOnInit(): void {
		this.titleInsurance$
			.pipe(untilDestroyed(this))
			.subscribe((titleInsurance) => {
				if (titleInsurance) {
					this.titleInsurance = titleInsurance;
					this.extendCreditToClient = titleInsurance.extendCreditToClient;
					this.amountDueByClient = titleInsurance.extendCreditToClient
						? titleInsurance.premiumWithCounselCreditApplied
						: titleInsurance.premium;
					this.hasQuote = !!titleInsurance.quote;
				}
			});

		this.state$.pipe(untilDestroyed(this)).subscribe(async (state) => {
			this.state = state;

			if (state && state !== FCTTitleInsuranceState.DRAFT) {
				this.loading = true;
				try {
					const { matterId, thirdPartyId } = this;
					await this.fctGetStatusService.updateStatus(matterId, thirdPartyId);
				} catch (e) {
					LogRocket.log('Error getting FCT status details', e);
				} finally {
					this.loading = false;
				}
			}

			this.setCurrentStep(this.state);
			this.changeStepperColor();
			if (this.state === FCTTitleInsuranceState.CANCELLED) {
				this.steps.pop();
				this.steps.pop();
				this.steps.push({ title: 'Cancelling', failed: true });
				this.steps.push({ title: 'Cancelled', failed: true });
				this.changeStepperColor('failed');
			}
			if (
				this.state === FCTTitleInsuranceState.WAITING_FOR_DOCUMENTS ||
				this.state === FCTTitleInsuranceState.PENDING_UPDATE ||
				this.state === FCTTitleInsuranceState.ON_HOLD ||
				this.state === FCTTitleInsuranceState.DRAFT
			) {
				this.changeStepperColor('warning');
			}
		});

		combineLatest([
			this.matter$,
			this.clients$,
			this.mortgages$,
			this.matterTitles$,
		])
			.pipe(untilDestroyed(this))
			.subscribe(([_matter, _clients, _mortgages, _matterTitles]) => {
				if (!_matter) {
					return;
				}

				const propertyType = _matter.propertyDetails?.propertyType?.type;
				const propertyUsage = _matter.propertyDetails?.propertyType?.usage;
				this.propertyUsage = propertyUsage;
				this.isCommercial = propertyUsage === PropertyUsage.COMMERCIAL;

				this.outOfOntario = !isRegionOntario(
					_matter?.propertyDetails?.address?.province,
				);

				this.invalidProperty =
					(propertyUsage === PropertyUsage.RESIDENTIAL &&
						propertyType === PropertyType.FARM_LAND) ||
					propertyType === PropertyType.OTHER;

				this.missing = this.validateFctDataService.getSubmitDataMissingSections(
					_matter,
					_clients,
					_mortgages,
					_matterTitles,
				);

				this.orderValid =
					this.missing?.length === 0 &&
					!this.invalidProperty &&
					!this.isCommercial &&
					!this.outOfOntario;
			});

		this.route.queryParams
			.pipe(untilDestroyed(this))
			.subscribe((queryparams) => {
				const { modal } = queryparams;

				if (modal === 'submit') {
					this.openModal();
				}
			});
	}

	viewBreakdown() {
		this.dialog.open(CostBreakdownComponent);
	}

	removeTitleInsurance() {
		this.matterDispatch.SetTitleInsurance(this.matterId, BlankTitleInsurance);
	}

	selectInsuranceProvider() {
		this.dialog.open(AddTitleInsuranceComponent);
	}

	openModal(): void {
		this.dialog.open(FctPortalComponent);
	}

	cancel(): void {
		this.dialog
			.open(FctCancelModalComponent, {
				data: {
					state: this.state,
				},
			})
			.afterClosed()
			.pipe(
				untilDestroyed(this),
				filter((result) => !!result),
			)
			.subscribe(() => {
				if (this.state) {
					this.openModal();
				} else {
					this.matterDispatch.SetTitleInsurance(
						this.matterId,
						this.blankTitleInsurance,
					);
				}
			});
	}

	changeStepperColor(state?: string): void {
		switch (state) {
			case 'failed':
				this.steps = this.steps.map((step) => ({
					...step,
					failed: true,
					warning: false,
				}));
				break;
			case 'warning':
				this.steps = this.steps.map((step) => ({
					...step,
					failed: false,
					warning: true,
				}));
				break;
			default:
				this.steps = this.steps.map((step) => ({
					...step,
					failed: false,
					warning: false,
				}));
				break;
		}
	}

	setCurrentStep = (status: FCTTitleInsuranceState): void => {
		switch (status) {
			case FCTTitleInsuranceState.DRAFT:
				this.currentStep = 0;
				break;
			case FCTTitleInsuranceState.PENDING_UPDATE:
			case FCTTitleInsuranceState.ON_HOLD:
			case FCTTitleInsuranceState.WAITING_FOR_DOCUMENTS:
			case FCTTitleInsuranceState.PENDING_CANCELLATION:
				this.currentStep = 1;
				break;
			case FCTTitleInsuranceState.IN_UNDERWRITING:
			case FCTTitleInsuranceState.PENDING_PROCESSING:
				this.currentStep = 1;
				break;
			case FCTTitleInsuranceState.CANCELLED:
				this.currentStep = 1;
				break;
			case FCTTitleInsuranceState.COMPLETED:
				this.currentStep = 2;
				break;
		}
	};

	showBreakdown(): void {
		const data: FCTTitleInsuranceCostBreakdownData = {
			type: 'QUOTE',
		};
		this.dialog.open(CostBreakdownComponent, { data });
	}
}
